import { Component, Inject, forwardRef, Input, Output, EventEmitter, ViewChild, OnChanges, SimpleChanges, AfterViewInit, ElementRef, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BaseWidget, NgAisConfigureModule, NgAisInstantSearch } from 'angular-instantsearch';
import { SearchParameters } from 'angular-instantsearch/instantsearch/instantsearch';
import { connectAutocomplete } from 'instantsearch.js/es/connectors';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { AutocompleteScrollDirective } from './autocomplete-scroll.directive';
import { Helper } from 'src/app/shared/utility/Helper';

@Component({
  selector: 'app-algolia-autocomplete',
  templateUrl: './algolia-autocomplete.component.html',
  styleUrls: ['./algolia-autocomplete.component.scss']
})
export class AlgoliaAutocompleteComponent extends BaseWidget implements OnChanges, AfterViewInit, OnDestroy {
  @Input() propValue: string = 'objectID';
  @Input() propDisplay: string = 'displayName';
  @Input() inputKeyword: string = '';
  @Input() configuration: SearchParameters = null;

  @Output() configurationChange = new EventEmitter<SearchParameters>();
  @Output() onQuerySuggestionClick = new EventEmitter<string>();
  @Output() onQueryEnter = new EventEmitter<string>();
  @Output() onQueryClean = new EventEmitter<void>();
  @Output() onRefresh = new EventEmitter<void>();

  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
  @ViewChild('inputSearch') inputSearch: ElementRef;
  @ViewChild(AutocompleteScrollDirective) autoCompleteScroll: AutocompleteScrollDirective;
  @ViewChild('searchContainer') searchContainer: ElementRef;
  @ViewChild('auto') autocompleteElement: MatAutocomplete;

  state: {
    query: string;
    refine: Function;
    indices: any[];
    results: object;
  };
  isNext: boolean = false;
  disableNext: boolean = false;

  sizePage: number = 20;
  _onDestroy = new Subject();

  constructor(
    @Inject(forwardRef(() => NgAisInstantSearch))
    public instantSearchParent: NgAisInstantSearch,
    private cdr: ChangeDetectorRef
  ) {
    super('AutocompleteComponent');
  }

  public ngOnInit() {
    this.createWidget(connectAutocomplete, {});
    super.ngOnInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    document.documentElement.style.removeProperty('--autocomplete-width');
    let inputChange = changes.inputKeyword;
    if (inputChange && inputChange.currentValue != inputChange.previousValue) {
      this.inputKeyword = inputChange.currentValue;
    }
  }

  public ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.inputKeyword)
        this.refreshAlgoliaSearch(this.inputKeyword);
    }, 500);

    fromEvent(this.inputSearch.nativeElement, 'keyup')
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((x: KeyboardEvent) => this.handleChange(x));

    this.instantSearchParent.change.asObservable()
      .pipe(
        debounceTime(500),
        tap(x => {
          try {
            if (this.autoCompleteScroll?.eleAutocomplete && !this.isNext)
              this.autoCompleteScroll.eleAutocomplete.scrollTo({ top: 0, behavior: "smooth" });

            this.isNext = false;
            let firstIndices = this.state?.indices[0];
            if (firstIndices?.results?.hits?.length == firstIndices?.results?.nbHits)
              this.disableNext = true;
          }
          catch (ex) {
            console.error(ex);
          }
        }),
        takeUntil(this._onDestroy)
      ).subscribe();
  }

  public handleChange($event: KeyboardEvent) {
    this.configuration.hitsPerPage = this.sizePage;
    this.refreshAlgoliaSearch(($event.target as HTMLInputElement).value);
  }

  public handleEnter($event: KeyboardEvent) {
    let query = ($event.target as HTMLInputElement).value;
    if (query) {
      this.autocomplete.closePanel();
      this.onQueryEnter.emit(query);
    }
  }

  public handleClick(query: string) {
    // Replace Special Character
    var elem = document.createElement('textarea');
    elem.innerHTML = query;
    query = elem.innerText;

    if (query) {
      this.autocomplete.closePanel();
      this.refreshAlgoliaSearch(query);
      this.onQueryEnter.emit(query);
    }
  }

  public handleClean() {
    this.inputKeyword = "";
    this.refreshAlgoliaSearch("");
    this.onQueryClean.emit();
  }

  public refreshAlgoliaSearch(key: string) {
    this.disableNext = false;
    this.state.refine(key, this.configuration);
  }

  public handleRefresh() {
    this.refreshAlgoliaSearch(this.inputKeyword);
    this.onRefresh.emit();
  }

  onScroll() {
    if (!this.isNext && this.configuration) {
      this.isNext = true;
      this.configuration.hitsPerPage += this.sizePage;
      this.refreshAlgoliaSearch(this.inputKeyword);
    }
  }

  opened() {
    this.setAutocompleteStyle();
  }

  private setAutocompleteStyle() {
    const containerRect = this.searchContainer.nativeElement.getBoundingClientRect();
    const width = Math.max(containerRect.width, 400);
    
    requestAnimationFrame(() => {
      const panel = this.autocompleteElement.panel?.nativeElement;
      const parentPanel = panel?.parentElement;
      
      if (!panel || !parentPanel) return;
      
      panel.style.setProperty('width', `${width}px`, 'important');
      panel.style.setProperty('min-width', `${width}px`, 'important');
      parentPanel.style.setProperty('width', `${width}px`, 'important');
      parentPanel.style.setProperty('min-width', `${width}px`, 'important');

      // parentPanel.style.setProperty('transform', `translateX(${offset}px)`, 'important');
      
      document.documentElement.style.setProperty('--autocomplete-width', `${width}px`);
    });
  }
}
